import React, { useState } from "react";
import { Progress, Button, Input, Divider, Select, Spin, message } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { LuBuilding2 } from "react-icons/lu";
import { MdOutlineVilla } from "react-icons/md";
import { FiHome } from "react-icons/fi";
import { FaRegMap } from "react-icons/fa";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { IoMdTrendingUp } from "react-icons/io";
import { LuDollarSign } from "react-icons/lu";
import { BsPerson } from "react-icons/bs";
import { createEnquiry } from "../Actions/enquiryAction";
import { useDispatch, useSelector } from "react-redux";

const QuizModal = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const dispatch = useDispatch();
  const {isCreateEnquiryLoading} = useSelector( (state) => state.enquiries);
  const [answers, setAnswers] = useState({
    propertyType: null,
    purpose: null,
    budget: null,
    timeline: null,
    full_name: "",
    phone: "",
    email: "",
    source: "",
  });

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => s - 1);

  const updateAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return !!answers.propertyType;
      case 2:
        return !!answers.purpose;
      case 3:
        return !!answers.budget;
      case 4:
        return !!answers.timeline;
      case 5:
        return (
          answers?.name?.trim() !== "" &&
          answers?.phone?.trim() !== "" &&
          answers?.source?.trim() !== ""
        );

      default:
        return false;
    }
  };

  const QUIZ_OPTIONS = {
    propertyType: [
      { label: "Flats", icon: LuBuilding2 },
      { label: "Bunglows", icon: FiHome },
      { label: "Plot", icon: FaRegMap },
      { label: "Weekend Villa & Plot", icon: MdOutlineVilla },
    ],

    purpose: [
      { label: "Personal Use", icon: FiHome },
      { label: "Investment", icon: IoMdTrendingUp },
      { label: "Selling a Property", icon: LuDollarSign },
    ],

    budget: [
      { label: "Under ₹1 Cr", icon: LuDollarSign },
      { label: "₹1–2 Cr", icon: LuDollarSign },
      { label: "₹2–4 Cr", icon: LuDollarSign },
      { label: "₹4 Cr+", icon: LuDollarSign },
      { label: "Flexible", icon: AiOutlineQuestionCircle },
    ],

    locations: [
      { label: "Science City" },
      { label: "Thaltej" },
      { label: "Sindhubhavan Road" },
      { label: "Bopal" },
      { label: "Open to Suggestions" },
    ],

    timeline: [
      { label: "Immediately", icon: ClockCircleOutlined },
      { label: "1–3 Months", icon: BsPerson },
      { label: "3–6 Months", icon: BsPerson },
      { label: "Just Exploring", icon: AiOutlineQuestionCircle },
    ],
  };

  const renderOptions = (
    options,
    valueKey,
    isMulti = false,
    hideIcon = false
  ) =>
    options.map(({ label, icon: Icon }) => {
      const isActive = isMulti
        ? answers[valueKey].includes(label)
        : answers[valueKey] === label;

      return (
        <div
          key={label}
          className={`quiz-option ${isActive ? "active" : ""}`}
          onClick={() =>
            isMulti
              ? updateAnswer(
                  valueKey,
                  isActive
                    ? answers[valueKey].filter((v) => v !== label)
                    : [...answers[valueKey], label]
                )
              : updateAnswer(valueKey, label)
          }
        >
          {!hideIcon && (
            <span className="quiz-icon">
              <Icon />
            </span>
          )}
          <span className="quiz-label">{label}</span>
        </div>
      );
    });

  const handleSubmit = () => {
    const data = {
      property_type: answers.propertyType,
      purpose: answers.purpose,
      budget: answers.budget,
      locations: answers.locations,
      timeline: answers.timeline,
      full_name: answers.name,
      phone: answers.phone,
      email: answers.email,
      source: answers.source,
    };
    dispatch(createEnquiry(data, successCallback));
    
  };

  const successCallback =()=>{
    message.success("Your enquiry has been submitted successfully!");
    onClose();
  }

  return (
    <Spin spinning={isCreateEnquiryLoading}>
    <div className="quiz-modal">
      {/* HEADER */}
      <div className="quiz-header">
        <h3>Find Your Ideal Property</h3>
        <Progress
          percent={(step / 5) * 100}
          showInfo={false}
          strokeColor="#1c3988"
        />
        <span className="quiz-step">Step {step} of 5</span>
      </div>
      <Divider className="quiz-divider" />
      {/* BODY */}
      <div className="quiz-body">
        {/* STEP 1 */}
        {step === 1 && (
          <>
            <h4>What are you looking for?</h4>
            <div className="quiz-options">
              {renderOptions(QUIZ_OPTIONS.propertyType, "propertyType")}
            </div>
          </>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <>
            <h4>What's the purpose?</h4>
            <div className="quiz-options">
              {renderOptions(QUIZ_OPTIONS.purpose, "purpose")}
            </div>
          </>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <>
            <h4>What's your budget range?</h4>
            <div className="quiz-options">
              {renderOptions(QUIZ_OPTIONS.budget, "budget")}
            </div>
          </>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <>
            <h4>When do you plan to make a decision?</h4>
            <div className="quiz-options">
              {renderOptions(QUIZ_OPTIONS.timeline, "timeline")}
            </div>
          </>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <>
            <h4>Almost there!</h4>
            <p className="quiz-helper-text">
              Enter your details so we can reach out with personalized
              recommendations.
            </p>

            {/* Name */}
            <div className="quiz-field">
              <label className="quiz-label-text">
                Name <span>*</span>
              </label>
              <Input
                placeholder="Your full name"
                value={answers.name}
                onChange={(e) => updateAnswer("name", e.target.value)}
              />
            </div>

            {/* Phone */}
            <div className="quiz-field">
              <label className="quiz-label-text">
                Phone Number <span>*</span>
              </label>
              <Input
                placeholder="+91 98XXXXXXX"
                value={answers.phone}
                onChange={(e) => updateAnswer("phone", e.target.value)}
              />
            </div>

            {/* Email */}
            <div className="quiz-field">
              <label className="quiz-label-text">Email (Optional)</label>
              <Input
                placeholder="your@email.com"
                value={answers.email}
                onChange={(e) => updateAnswer("email", e.target.value)}
              />
            </div>

            {/* Source */}
            <div className="quiz-field">
              <label className="quiz-label-text">
                How did you hear about us? <span>*</span>
              </label>
              <Select
                placeholder="Select an option"
                style={{ width: "100%" }}
                value={answers.source || undefined}
                onChange={(value) => updateAnswer("source", value)}
                options={[
                  { value: "Google Search", label: "Google Search" },
                  { value: "Instagram", label: "Instagram" },
                  { value: "Facebook", label: "Facebook" },
                  { value: "Friend / Referral", label: "Friend / Referral" },
                  { value: "Broker", label: "Broker" },
                  {
                    value: "On-ground Visit / Site Visit",
                    label: "On-ground Visit / Site Visit",
                  },
                  { value: "Other", label: "Other" },
                ]}
              />
            </div>
          </>
        )}
      </div>
      <Divider className="quiz-divider" />
      {/* FOOTER */}
      <div className="quiz-footer">
        <Button type="link" onClick={back} disabled={step === 1}>
          Back
        </Button>

        {step < 5 ? (
          <Button type="primary" onClick={next} disabled={!isStepValid()}>
            Next
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={!isStepValid()}
          >
            Submit
          </Button>
        )}
      </div>
    </div></Spin>
  );
};

export default QuizModal;
